import {pushEvent} from "tracking/core/dispatcher";
import type {
    DonationBeginPayload,
    DonationSubmitErrorPayload,
    DonationSubmitPayload,
    PushOptions,
    SelectDonationAmountPayload,
    SelectDonationRhythmPayload,
    SelectDonationTypePayload,
    SelectPaymentMethodPayload,
    SelectPriorityPayload,
    SelectSponsorshipTypePayload,
} from "../core/types";
import {rhythmToString, typeToString} from "../utils/donationStringMappers";
import {EVENT_NAMES} from "tracking/core/eventNames";

export function dataLayerDonationBegin(options?: PushOptions) {
    pushEvent(EVENT_NAMES.DONATION_BEGIN, {} as DonationBeginPayload, options);
}

export function dataLayerSelectDonationAmount(
    donation_amount?: number | null,
    currency?: string | null,
    options?: PushOptions
) {
    const payload: SelectDonationAmountPayload = {
        donation_amount: donation_amount ?? undefined,
        currency: currency ?? undefined,
    };
    pushEvent(EVENT_NAMES.SELECT_DONATION_AMOUNT, payload, options);
}

export function dataLayerSelectDonationType(
    donation_type?: string | null,
    options?: PushOptions
) {
    const payload: SelectDonationTypePayload = {
        donation_type: donation_type ?? undefined,
    };
    pushEvent(EVENT_NAMES.SELECT_DONATION_TYPE, payload, options);
}

export function dataLayerSelectSponsorshipType(
    donation_type?: string | null,
    options?: PushOptions
) {
    const payload: SelectSponsorshipTypePayload = {
        donation_type: donation_type ?? undefined,
    };
    pushEvent(EVENT_NAMES.SELECT_SPONSORSHIP_TYPE, payload, options);
}

export function dataLayerSelectPriority(
    donation_priority?: string | null,
    options?: PushOptions
) {
    const payload: SelectPriorityPayload = {
        donation_priority: donation_priority ?? undefined,
    };
    pushEvent(EVENT_NAMES.SELECT_PRIORITY, payload, options);
}

export function dataLayerSelectPaymentMethod(
    donation_payment_method?: string | null,
    options?: PushOptions
) {
    const payload: SelectPaymentMethodPayload = {
        donation_payment_method: donation_payment_method ?? undefined,
    };
    pushEvent(EVENT_NAMES.SELECT_PAYMENT_METHOD, payload, options);
}

export function dataLayerSelectDonationRhythm(
    donation_rhythm?: string | null,
    options?: PushOptions
) {
    const payload: SelectDonationRhythmPayload = {
        donation_rhythm: donation_rhythm ?? undefined,
    };
    pushEvent(EVENT_NAMES.SELECT_DONATION_RHYTHM, payload, options);
}

export function dataLayerDonationSubmit(
    params: {
        currency?: string | null;
        donation_id?: string | null;
        donation_amount?: number | null;
        donation_rhythm?: string | null;
        donation_priority?: string | null;
        donation_payment_method?: string | null;
        donation_type?: string | null;
    },
    options?: PushOptions
) {
    const payload: DonationSubmitPayload = {
        currency: params.currency ?? undefined,
        donation_id: params.donation_id ?? undefined,
        donation_amount: params.donation_amount ?? undefined,
        donation_rhythm: params.donation_rhythm ?? undefined,
        donation_priority: params.donation_priority ?? undefined,
        donation_payment_method: params.donation_payment_method ?? undefined,
        donation_type: params.donation_type ?? undefined,
    };
    pushEvent(EVENT_NAMES.DONATION_SUBMIT, payload, options);
}

export function dataLayerDonationSubmitError(
    params: {
        currency?: string | null;
        donation_amount?: number | null;
        donation_type?: string | null;
        error_message?: string | null;
    },
    options?: PushOptions
) {
    const payload: DonationSubmitErrorPayload = {
        currency: params.currency ?? undefined,
        donation_amount: params.donation_amount ?? undefined,
        donation_type: params.donation_type ?? undefined,
        error_message: params.error_message ?? undefined,
    };
    pushEvent(EVENT_NAMES.DONATION_SUBMIT_ERROR, payload, options);
}

export function dataLayerDonationWidget(
    params: {
        currency?: string | null;
        donation_amount?: number | null;
        donation_rhythm?: string | null;
        donation_priority?: string | null;
        donation_payment_method?: string | null;
        donation_type?: string | null;
    },
    options?: PushOptions
) {
    const payload = {
        currency: params.currency ?? undefined,
        donation_amount: params.donation_amount ?? undefined,
        donation_rhythm: params.donation_rhythm ?? undefined,
        donation_priority: params.donation_priority ?? undefined,
        donation_payment_method: params.donation_payment_method ?? undefined,
        donation_type: params.donation_type ?? undefined,
    } as any;
    pushEvent(EVENT_NAMES.DONATION_WIDGET, payload, options);
}

// Helper to transform React Hook Form data into a donation_widget payload
// and send the corresponding tracking event
export function trackDonationWidgetFromFormData(formData?: any, options?: PushOptions) {
    try {
        const amount = formData?.amount;
        const rhythm = rhythmToString(formData?.donationRhythm);
        const typeStr = typeToString(formData?.donationType);
        const priority = formData?.priority;
        const payment = formData?.paymentMethod != null ? String(formData.paymentMethod) :
            undefined;
        dataLayerDonationWidget(
            {
                currency: "EUR",
                donation_amount: typeof amount === "number" ? amount : undefined,
                donation_rhythm: rhythm,
                donation_priority: priority ?? undefined,
                donation_payment_method: payment,
                donation_type: typeStr,
            },
            options
        );
    } catch {
        /* noop */
    }
}
